import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { BrowserOverlay } from '../BrowserOverlay';
import { ScanGallery } from './ScanGallery';
import { useScanGallery } from './useScanGallery';
import type { ScanImageSets } from './useScanImageSets';

const FULLSCREEN_ID_SUFFIX = '-fullscreen';

// Runs after the layer's own focus restore, so focus lands on the enlarge button whatever opened it.
function useFocusOnExit(fullscreen: boolean, buttonRef: RefObject<HTMLButtonElement>): void {
    const wasFullscreen = useRef(fullscreen);
    useEffect(() => {
        if (wasFullscreen.current && !fullscreen) buttonRef.current?.focus();
        wasFullscreen.current = fullscreen;
    }, [fullscreen, buttonRef]);
}

// The inline card stays mounted under the layer, so the pane keeps its height and scroll position.
export function ScanImagesSection({ sets }: { sets: ScanImageSets }) {
    const gallery = useScanGallery(sets);
    const [fullscreen, setFullscreen] = useState(false);
    const enlargeRef = useRef<HTMLButtonElement>(null);
    const fullscreenRootRef = useRef<HTMLElement>(null);
    const exitFullscreen = useCallback(() => setFullscreen(false), []);
    useFocusOnExit(fullscreen, enlargeRef);

    return (
        <>
            <ScanGallery
                gallery={gallery}
                idBase={gallery.baseId}
                fullscreen={false}
                toggleRef={enlargeRef}
                onToggleFullscreen={() => setFullscreen(true)}
            />
            {fullscreen && (
                <BrowserOverlay
                    label={`${gallery.set.tab.label}, full screen`}
                    onClose={exitFullscreen}
                    initialFocusRef={fullscreenRootRef}
                    className="bg-gray-50 p-5"
                >
                    <ScanGallery
                        gallery={gallery}
                        idBase={`${gallery.baseId}${FULLSCREEN_ID_SUFFIX}`}
                        fullscreen
                        rootRef={fullscreenRootRef}
                        onToggleFullscreen={exitFullscreen}
                    />
                </BrowserOverlay>
            )}
        </>
    );
}
